import { MoreVert } from '@mui/icons-material';
import {Dropdown, Menu, MenuButton, MenuItem} from '@mui/joy';
import React, {useContext, useEffect, useState} from "react";
import RoundedImage from "../RoundedImage";
import {WebRTCContext} from "../../../contexts/WebRTCContext";

const SessionCard = ({ session, onPlay, onDelete, openMenu, setOpenMenu }) => {

    const { rtcSession } = useContext(WebRTCContext);
    const [imagesMap, setImagesMap] = useState({});
    const { id, start_ms, end_ms, previews } = session;

    // Format times
    const formatDate = (ms) => {
        const date = new Date(ms);
        return date.toLocaleTimeString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: false,
        })
    };

    const handleMenuClick = (event) => {
        // Toggle the menu
        if (openMenu === id) {
            setOpenMenu(null); // Close the menu if it's already open
        } else {
            setOpenMenu(id); // Open the menu for this item
        }
    };

    function onDownload(recording) {
        console.log('Download recording TODO');
    }

    useEffect(() => {
        const imageLoadingManager = rtcSession?.imageLoadingManager;
        if (!imageLoadingManager || !previews) return;

        // For each preview in the session, request its image
        // and store the "cancel" callback to call in cleanup.
        const cleanupFns = previews.map((preview) => {
            const listener = {
                onSuccess: (imageData) => {
                    setImagesMap((prevMap) => ({
                        ...prevMap,
                        [preview.id]: imageData,
                    }));
                    console.log(`SessionCard - preview ${preview.id} image load succeeded`);
                },
                onFailure: (error) => {
                    console.error(`SessionCard - preview ${preview.id} image load failed`, error);
                },
            };

            // Fire off the request
            imageLoadingManager.requestImage(
                preview.id,
                imageLoadingManager.ImageTypes.SESSION_PREVIEW,
                listener
            );

            // Return the cancel function for cleanup
            return () => {
                imageLoadingManager.cancelRequest(preview.id, listener);
            };
        });

        // Cleanup: cancel all requests if previews change or component unmounts
        return () => {
            cleanupFns.forEach((cancelFn) => cancelFn());
        };
    }, [rtcSession, previews]);

    return (
        <div style={styles.row}>
            <div style={styles.verticalLine}>
                <div style={styles.dot}></div>
            </div>
            <div style={styles.card}>
                <div style={styles.cardHeader}>
                    <div style={styles.headerTextBlock}>
                        <p style={styles.text}>
                            <strong>Session {id}</strong>  {formatDate(start_ms)}
                        </p>
                        {/*
                          If you want to show session start/end times:
                          <p style={styles.text}>
                              {formatTime(start_ms)} - {formatTime(end_ms)}
                          </p>
                        */}
                    </div>

                    {/* Menu for 3-dots icon */}
                    <Dropdown>
                        <MenuButton
                            variant={'plain'}
                            onClick={handleMenuClick}
                            style={{ cursor: 'pointer' }}
                        >
                            <MoreVert />
                        </MenuButton>
                        {openMenu === id && (
                            <Menu>
                                <MenuItem onClick={() => { onDelete(session); setOpenMenu(null); }}>
                                    Delete
                                </MenuItem>
                                <MenuItem onClick={() => { onDownload(session); setOpenMenu(null); }}>
                                    Download
                                </MenuItem>
                            </Menu>
                        )}
                    </Dropdown>
                </div>

                {/* Preview images block, arranged horizontally */}
                <div style={styles.previewContainer}>
                    {previews && previews.length > 0 ? (
                        previews.map((preview) => {
                            return (
                                <RoundedImage
                                    key={preview.id}
                                    src={
                                        imagesMap[preview.id] ||
                                        'https://images.unsplash.com/photo-1527549993586-dff825b37782?auto=format&h=160'
                                    }
                                    alt="Preview"
                                    width="140px"
                                    height="140px"
                                    style={styles.image}
                                />
                            );
                        })
                    ) : (
                        <div>No sessions available</div>
                    )}
                </div>
            </div>
        </div>
    );
};

const styles = {
    row: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        position: 'relative',
    },
    verticalLine: {
        position: 'absolute',
        left: '-8px',
        top: '0',
        bottom: '0',
        width: '1.5px',
        backgroundColor: '#d3d1d1',
    },
    dot: {
        position: 'absolute',
        left: '-5px',
        top: '40%',
        width: '4.5px',
        height: '4px',
        borderRadius: '50%',
        backgroundColor: '#333',
        border: '4px solid white',
    },

    // Updated card to a column layout so the preview images go below the header
    card: {
        display: 'flex',
        flexDirection: 'column',
        padding: '10px',
        borderRadius: '8px',
        position: 'relative',
        boxSizing: 'border-box',
        width: '100%',
        maxWidth: '600px',
    },

    // Header block row: left side text, right side 3-dot menu
    cardHeader: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        width: '100%',
        marginBottom: '8px',
    },
    headerTextBlock: {
        display: 'flex',
        flexDirection: 'column',
    },

    // Container for preview images, in a row
    previewContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: '10px',
        flexWrap: 'wrap', // if you'd like them to wrap on smaller widths
    },

    image: {
        width: '100%',
        height: '100%',
        borderRadius: '8px',
    },
    text: {
        fontSize: '14px',
        margin: '5px 0',
    },
};

export default SessionCard;